import * as React from 'react';
import { CommissionStatement } from '../../MedicareProAPI/Entities/CommissionStatement/CommissionStatement';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import {
  CommissionStatementRepository,
  CommissionStatementStatus
} from '../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import { Utilities } from '../../MedicareProAPI/Utilities/Utilities';
import { CommissionStatementForm } from '../Components/CommissionStatement/CommissionStatementForm';
import StyledModal from '../Components/StyledModal';
import { UserContext } from '../Context/UserContext';

/**
 * Props interface for the AgentCommissionStatementsPanel component
 */
export interface AgentCommissionStatementsPanelProps {
  /** The user whose commission statements are being managed */
  user: User;
}

/**
 * Agent Commission Statements Panel Component
 *
 * A comprehensive panel for managing commission statements for agents.
 * Provides functionality to upload, view, edit, and filter commission statements.
 * Features a sidebar list of statements and a main form area for detailed editing.
 *
 * @param props - The component props
 * @returns JSX.Element - The rendered commission statements panel
 */
export default function AgentCommissionStatementsPanel(
  props: AgentCommissionStatementsPanelProps
): React.JSX.Element {
  // Context and user data
  const userData = React.useContext(UserContext);

  // Commission statements state
  const [commissionStatements, setCommissionStatements] =
    React.useState<Array<CommissionStatement>>();
  const [commissionStatementsLoading, setCommissionStatementsLoading] =
    React.useState<boolean>(true);

  // Modal state for uploading new statements
  const [commissionStatementModal, setCommissionStatementModal] = React.useState<boolean>(false);
  const [modalStatement, setModalStatement] = React.useState<CommissionStatement>();

  // Currently selected statement for editing
  const [formStatement, setFormStatement] = React.useState<CommissionStatement>();

  // Filter state for year and month selection
  const [filterYear, setFilterYear] = React.useState<number | undefined>(
    Number(new Date().getFullYear())
  );
  const [filterMonth, setFilterMonth] = React.useState<number | undefined>(
    Number(new Date().getMonth()) + 1
  );

  // Repository instance for API calls
  const commissionStatementRepository = new CommissionStatementRepository();
  commissionStatementRepository.setAccessToken(userData.userAccessToken);

  /**
   * Builds a date range for filtering based on year and optional month
   *
   * @param filterYear - The year to filter by
   * @param filterMonth - Optional month to filter by
   * @returns Object containing from and to date strings
   */
  function buildDateRange(filterYear: number, filterMonth?: number) {
    let from: string;
    let to: string;

    if (filterMonth) {
      // Ensure month is 2 digits
      const month = filterMonth.toString().padStart(2, '0');

      // First day of month
      from = `${filterYear}-${month}-01`;

      // Last day of month — use JS date trick to get the last day
      const lastDay = new Date(filterYear, filterMonth, 0).getDate();
      to = `${filterYear}-${month}-${lastDay}`;
    } else {
      from = `${filterYear}-01-01`;
      to = `${filterYear}-12-31`;
    }

    return { from, to };
  }

  /**
   * Fetches commission statements from the API with optional filtering
   *
   * @param selectFirstStatement - Whether to automatically select the first statement after loading
   */
  const getCommissionStatements = (selectFirstStatement: boolean): void => {
    const options = {
      criteria: {
        date: buildDateRange(filterYear, filterMonth),
      },
    };

    commissionStatementRepository
      .getCommissionStatements(userData.userId, options)
      .then((commissionStatements) => {
        setCommissionStatements(commissionStatements.getItems());

        // Auto-select first statement if requested and statements exist
        if (selectFirstStatement && commissionStatements.getTotalItems() > 0) {
          setFormStatement(commissionStatements.getItem(0));
        }

        setCommissionStatementsLoading(false);
      });
  };

  /**
   * Effect hook to reload commission statements when filters change
   */
  React.useEffect(() => {
    getCommissionStatements(true);
  }, [filterMonth, filterYear]);

  /**
   * Creates a new commission statement and opens the upload modal
   */
  const createNewStatement = () => {
    const newStatement = new CommissionStatement();
    newStatement.setUser(props.user);

    setCommissionStatementModal(true);
    setModalStatement(newStatement);
  };

  /**
   * Selects a commission statement for editing in the main form
   *
   * @param selectedStatement - The statement to select
   */
  const selectStatement = (selectedStatement: CommissionStatement) => {
    setFormStatement(selectedStatement);
  };

  /**
   * Handles commission statement deletion
   * Clears the selected statement and refreshes the list
   */
  const handleStatementDelete = (): void => {
    setFormStatement(null);
    getCommissionStatements(true);
  };

  /**
   * Handles commission statement save/update
   * Updates the selected statement and refreshes the list without auto-selection
   *
   * @param statement - The saved statement
   * @returns Promise resolving to the saved statement
   */
  const handleStatementSave = (statement: CommissionStatement): Promise<CommissionStatement> => {
    setFormStatement(statement);
    getCommissionStatements(false);

    setFilterMonth(statement.getDate().getMonth() + 1);
    setFilterYear(statement.getDate().getFullYear());

    return Promise.resolve(statement);
  };

  /**
   * Handles commission statement processing after upload
   * Sets the processed statement as selected and closes the modal
   *
   * @param statement - The processed statement
   * @returns Promise resolving to the processed statement
   */
  const handleStatementProcess = (statement: CommissionStatement): Promise<CommissionStatement> => {
    setFormStatement(statement);
    setCommissionStatementModal(false);

    return Promise.resolve(statement);
  };

  /**
   * Handles year filter selection change
   *
   * @param event - The select change event
   */
  const handleChangeFilterYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setFilterYear(Number(selectedValue));
  };

  /**
   * Handles month filter selection change
   *
   * @param event - The select change event
   */
  const handleChangeFilterMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setFilterMonth(Number(selectedValue));
  };

  return (
    <>
      {/* Upload Statement Modal */}
      <StyledModal
        size="xl"
        title={'Upload Statement'}
        footerCloseButton={false}
        show={commissionStatementModal}
        onClose={(e) => setCommissionStatementModal(false)}
      >
        <CommissionStatementForm
          user={props.user}
          commissionStatement={modalStatement}
          onSave={handleStatementSave}
          onProcess={handleStatementProcess}
        />
      </StyledModal>

      {/* Main Content Card */}
      <div className="card pb-0 pt-0" style={{ height: 'auto' }}>
        <div className="row">
          {/* Loading State */}
          {commissionStatementsLoading === true && (
            <div className="col-12">
              <p className="alert alert-info small m-2">
                Loading... <i className="fas fa-spinner fa-spin"></i>
              </p>
            </div>
          )}

          {/* Statements Content */}
          {commissionStatementsLoading === false && (
            <>
              {/* Left Sidebar - Statement List */}
              <div className="col-12 col-lg-2 col-lg-4 col-xl-2 pt-2">
                <div className="ml-2">
                  {/* Filter Controls */}
                  <div className="row">
                    <div className="col-6 pr-1">
                      <select
                        onChange={handleChangeFilterYear}
                        className="form-control form-control-sm mb-2"
                        value={filterYear}
                      >
                        <option>Statement Year</option>
                        {Utilities.getYears(15).map((year) => {
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="col-6 pl-1">
                      <select
                        disabled={!filterYear}
                        onChange={handleChangeFilterMonth}
                        className="form-control form-control-sm mb-2"
                        value={filterMonth}
                      >
                        <option>All Months</option>
                        {Utilities.getMonths().map((month, index) => {
                          return (
                            <option key={index} value={index + 1}>
                              {month}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Upload Button */}
                  <button onClick={(e) => createNewStatement()} className="col-12 pl-1 btn btn-success btn-sm mb-2">
                    <i className="far fa-plus-circle mr-1"></i> Upload Statement
                  </button>
                </div>

                {/* Empty State */}
                {commissionStatements.length === 0 && (
                  <p className="alert alert-info small ml-2 mt-0 mb-2">No statements to display</p>
                )}

                {/* Statement List */}
                {commissionStatements &&
                  commissionStatements.map((statement) => {
                    // Build CSS classes for statement card
                    const classList = [
                      'small',
                      'card',
                      'p-2',
                      'mb-2',
                      'cp',
                      'card-tall',
                      'border',
                      'border-info',
                      'ml-2',
                    ];
                    const classListInner = [
                      'badge',
                      'small',
                      'float-right',
                      'font-weight-normal',
                      'text-white',
                      'ml-1',
                    ];

                    // Highlight selected statement
                    if (formStatement && statement.getId() === formStatement.getId()) {
                      classList.push('bg-info');
                      classList.push('text-white');
                    }

                    // Color code by status
                    if (statement.getStatus() === CommissionStatementStatus.Completed) {
                      classListInner.push('bg-success');
                    } else if (statement.getStatus() === CommissionStatementStatus.Incomplete) {
                      classListInner.push('bg-warning');
                    }

                    return (
                      <div
                        key={statement.getId()}
                        onClick={(e) => selectStatement(statement)}
                        className={classList.join(' ')}
                      >
                        <div>
                          <span className={classListInner.join(' ')}>
                            {Utilities.capitalizeAll(statement.getStatus())}
                          </span>
                          <p className="mb-0">
                            <strong>{statement.getName()}</strong>
                          </p>
                          <p className="mb-0">{statement.getFileName()}</p>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="col-12 col-lg-8 col-xl-10 pt-2">
                <div className="row pr-2">
                  {/* Right Side - Statement Form */}
                  {commissionStatements.length > 0 && (
                    <div className="col-12">
                      {formStatement && (
                        <CommissionStatementForm
                          user={props.user}
                          commissionStatement={formStatement}
                          onSave={handleStatementSave}
                          onDelete={handleStatementDelete}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
